/**
 * LED Piece — デジタルドット × ストリートアート。
 *
 * 暗いコンクリート壁に "y16.dev" を LED ドットマトリクスとして打つ。
 * ドットの色はスプレーのネオン 3 色が文字の中を流れ、文字の下端からは
 * 塗料の垂れが伸びる。輪郭の外はステンシルのオーバースプレー霧。
 * 起動時は左から右へ吹き付けられるように点灯する。
 *
 * 全部 1 枚のフラグメントシェーダで描く。ジオメトリは画面を覆う板だけ。
 */
import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { SDF } from "./sdf-meta";

/** 文字が画面幅・画面高さに占める割合の上限。小さい方の制約が効く。 */
const FIT_W = 0.84;
const FIT_H = 0.4;

export type FrameState = {
  /** 経過秒。reduced-motion では固定値が来る。 */
  t: number;
  pointer: readonly [number, number];
  /** true なら登場アニメーションを飛ばして定常状態を描く。 */
  settled: boolean;
};

export type Stage = {
  render(state: FrameState): void;
  resize(width: number, height: number, dpr: number): void;
  dispose(): void;
};

const DOTS_FRAG = /* glsl */ `
precision highp float;

uniform vec2 uRes;
uniform float uTime;
uniform vec2 uPointer;
uniform sampler2D uSdf;
uniform vec2 uSdfRes;
uniform float uSdfRange;
uniform float uDripMax;
/** 画面ピクセル / テクセルの倍率。 */
uniform float uScale;
/** スプレーの吹き付け進行。0 で無、1 で全点灯。 */
uniform float uSettle;
uniform vec3 uInk[3];

out vec4 fragColor;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    s += a * vnoise(p);
    p = mat2(1.6, 1.2, -1.2, 1.6) * p;
    a *= 0.5;
  }
  return s;
}

/** 画面ピクセル座標を SDF のテクセル座標へ写す。y は反転。 */
vec2 toTexel(vec2 frag) {
  vec2 t = (frag - uRes * 0.5) / uScale;
  return vec2(t.x, -t.y) + uSdfRes * 0.5;
}

/** 生成時の sign(d) * sqrt(|d| / range) を戻す。テクセル単位、内側が正。 */
float decodeSdf(float v) {
  float s = v - 0.5;
  float a = 2.0 * abs(s);
  return sign(s) * a * a * uSdfRange;
}

vec2 field(vec2 texel) {
  vec2 uv = clamp(texel, vec2(0.5), uSdfRes - 0.5) / uSdfRes;
  return texture(uSdf, uv).rg;
}

/** スプレー缶のネオン。ノイズ値 0..1 を 3 色のゾーンに割る。 */
vec3 ink(float x) {
  vec3 c = mix(uInk[0], uInk[1], smoothstep(0.30, 0.46, x));
  return mix(c, uInk[2], smoothstep(0.58, 0.74, x));
}

/** 色ゾーンの元になる場。ゆっくり流れて文字の中を色が移動する。 */
float inkField(vec2 texel) {
  return fbm(texel * 0.011 + vec2(uTime * 0.028, -uTime * 0.017));
}

void main() {
  vec2 frag = gl_FragCoord.xy;
  vec2 texel = toTexel(frag);
  float sMin = min(uRes.x, uRes.y);

  // ---- コンクリート壁 ----
  vec2 wp = (frag - uRes * 0.5) / sMin;
  float stain = fbm(wp * 3.1 + 17.0);
  float grain = hash21(floor(frag / 2.0));
  vec3 col = vec3(0.020, 0.022, 0.030);
  col += vec3(0.012, 0.013, 0.018) * stain;
  col += vec3(0.006) * (grain - 0.5);
  // 壁の継ぎ目。水平の細い影を数本、うっすら。
  float seam = smoothstep(0.996, 1.0, sin(wp.y * 14.0 + 2.0));
  col *= 1.0 - seam * 0.4;

  float d = decodeSdf(field(texel).r);
  float n = inkField(texel);

  // ---- オーバースプレーの霧。輪郭の外側にだけ、色付きの粒を撒く ----
  float mistZone = smoothstep(-24.0, -2.0, d) * step(d, -0.5);
  float mist = step(0.90, hash21(floor(texel * 1.7))) * mistZone;
  col += ink(n) * mist * 0.15;
  // 缶の噴きだまり。文字裏の淡いハロー。
  col += ink(n) * exp(min(d, 0.0) / 26.0) * 0.06;

  // ---- デジタルグリッチ。まれに数フレームだけ行がずれる ----
  vec2 dotTexel = texel;
  {
    float win = floor(uTime * 0.5);
    float on = step(0.75, hash21(vec2(win, 11.7)));
    float burst = exp(-fract(uTime * 0.5) * 10.0);
    float row = floor(texel.y / 9.0);
    dotTexel.x += (hash21(vec2(row, win)) - 0.5) * 10.0 * on * burst;
  }

  // ---- LED ドット ----
  float pitch = 6.0;
  vec2 cell = (floor(dotTexel / pitch) + 0.5) * pitch;
  float cd = decodeSdf(field(cell).r);
  // 内側で満点、輪郭 1 セル分で絞る。ハーフトーンの縁。
  float ins = clamp((cd + pitch * 0.55) / (pitch * 1.1), 0.0, 1.0);

  // 吹き付けの進行。左から右へ、セルごとの粗い乱れを添えて。
  float sweep = uSettle * 1.3 - cell.x / uSdfRes.x - hash21(cell) * 0.18;
  float reveal = smoothstep(0.0, 0.08, sweep);
  // 点いた瞬間の煽り。吹かれた直後だけ強く光る。
  float pop = exp(-max(sweep, 0.0) * 7.0) * reveal * 1.6;

  // スプレー缶としてのポインタ。gl_FragCoord は y 上向きなので反転して合わせる。
  vec2 pFrag = vec2(uRes.x * 0.5 + uPointer.x * sMin, uRes.y * 0.5 - uPointer.y * sMin);
  float can = exp(-length(frag - pFrag) / (0.10 * sMin));

  float radius = pitch * 0.46 * ins * reveal * (1.0 + can * 0.16);
  float r = length(dotTexel - cell);
  float aa = fwidth(r) * 1.2;
  float dot = 1.0 - smoothstep(radius - aa, radius + aa, r);

  if (ins > 0.0) {
    vec3 c = ink(inkField(cell));
    // マーキーの帯。LED 看板の流れる光。
    float wave = sin((cell.x + cell.y) * 0.055 - uTime * 1.7) * 0.5 + 0.5;
    // LED の個体差のまたたき。
    float fl = 0.92 + 0.16 * hash21(cell + floor(uTime * 8.0));
    float bright = (0.95 + pow(wave, 3.0) * 1.1) * fl * (1.0 + can * 0.9) + pop;
    // ドット中心をわずかに熱く。レンズの効いた LED に見える。
    bright *= 0.85 + 0.3 * (1.0 - clamp(r / max(radius, 1e-3), 0.0, 1.0));
    col = mix(col, c * bright, dot);
  }

  // ---- 塗料の垂れ。文字最下端から下へ ----
  float g = field(texel).g;
  if (g > 0.004) {
    float t = 1.0 - g; // 0 = 縁、1 = dripMax 先
    float block = floor(texel.x / 3.0);
    float exists = step(0.62, hash21(vec2(block, 7.3)));
    float len = 0.25 + 0.75 * hash21(vec2(block, 3.1));
    // 吹き終えた列から垂れ始め、数秒かけて伸びる。
    float grow = len * clamp((uTime - 1.2) / 5.0, 0.0, 1.0) * uSettle;
    // 筋の細さ。ブロック中央だけを通す。
    float wx = abs(fract(texel.x / 3.0) - 0.5) * 3.0;
    float streak = smoothstep(1.05, 0.35, wx);
    float body = exists * streak * step(t, grow) * (1.0 - t * 0.55);
    // 先端の雫。少し膨らんで明るい。
    float head = exists * streak * smoothstep(0.045, 0.0, abs(t - grow)) * 0.8;
    // 色は垂れ始めた地点のインクを引きずる。
    vec3 c = ink(inkField(vec2(texel.x, texel.y - t * uDripMax)));
    col = mix(col, c * 1.05, clamp(body + head, 0.0, 1.0) * 0.92);
  }

  // ポインタ周りのスプレー霧。壁にも薄く粒が乗る。
  float pm = step(0.965, hash21(floor(frag / 2.0) + 3.7)) * can;
  col += ink(inkField(texel)) * pm * 0.5;

  // 画面の縁を落として中央へ視線を寄せる。
  col *= 1.0 - 0.4 * smoothstep(0.35, 1.15, length(wp));

  fragColor = vec4(col, 1.0);
}
`;

/** トーンマップ後に掛ける仕上げ。粒状感・周辺減光・縁の色ずれをまとめて 1 パスで。 */
const FinishShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: /* glsl */ `
    precision highp float;
    uniform sampler2D tDiffuse;
    uniform float uTime;
    varying vec2 vUv;

    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    void main() {
      vec2 d = vUv - 0.5;
      float r2 = dot(d, d);

      // 中央は像を保ち、縁だけ波長がずれる。レンズの色収差の近似。
      float ca = 0.005 * r2;
      vec3 col = vec3(
        texture2D(tDiffuse, vUv + d * ca).r,
        texture2D(tDiffuse, vUv).g,
        texture2D(tDiffuse, vUv - d * ca).b
      );

      // 周辺減光で視線を中央へ。
      col *= 1.0 - 0.36 * smoothstep(0.08, 0.5, r2);

      // 動く粒状感。静止画でも質感が出て、暗部の縞も散る。
      col += (hash(vUv * 1913.0 + fract(uTime) * 7.0) - 0.5) * 0.030;

      gl_FragColor = vec4(col, 1.0);
    }`,
};

export async function buildStage(canvas: HTMLCanvasElement): Promise<Stage> {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false, // ドットの縁はシェーダ内で解析的に AA するので MSAA は不要
    alpha: false,
    powerPreference: "high-performance",
  });
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const res = await fetch(SDF.bin);
  if (!res.ok) throw new Error(`${SDF.bin}: ${res.status}`);
  const data = new Uint8Array(await res.arrayBuffer());
  if (data.length !== SDF.width * SDF.height * 2) {
    throw new Error(`${SDF.bin}: expected ${SDF.width * SDF.height * 2} bytes, got ${data.length}`);
  }
  const sdfTex = new THREE.DataTexture(data, SDF.width, SDF.height, THREE.RGFormat, THREE.UnsignedByteType);
  sdfTex.minFilter = THREE.LinearFilter;
  sdfTex.magFilter = THREE.LinearFilter;
  sdfTex.needsUpdate = true;

  const material = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    uniforms: {
      uRes: { value: new THREE.Vector2(1, 1) },
      uTime: { value: 0 },
      uPointer: { value: new THREE.Vector2(0, 0) },
      uSdf: { value: sdfTex },
      uSdfRes: { value: new THREE.Vector2(SDF.width, SDF.height) },
      uSdfRange: { value: SDF.range },
      uDripMax: { value: SDF.dripMax },
      uScale: { value: 1 },
      uSettle: { value: 0 },
      // スプレー缶のネオン。THREE.Color は生成時に linear へ変換される。
      uInk: { value: [new THREE.Color(0xff2ea6), new THREE.Color(0x22d3ee), new THREE.Color(0xffe74a)] },
    },
    vertexShader: /* glsl */ `void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }`,
    fragmentShader: DOTS_FRAG,
    depthTest: false,
    depthWrite: false,
  });

  const scene = new THREE.Scene();
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  scene.add(quad);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  // HDR のまま bloom を通し、OutputPass でトーンマップしてから仕上げを掛ける。
  const target = new THREE.WebGLRenderTarget(2, 2, { type: THREE.HalfFloatType });
  const composer = new EffectComposer(renderer, target);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.38, 0.55, 0.85);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());
  const finish = new ShaderPass(FinishShader);
  composer.addPass(finish);

  const u = material.uniforms;

  return {
    resize(width, height, dpr) {
      renderer.setPixelRatio(dpr);
      renderer.setSize(width, height, false);
      composer.setPixelRatio(dpr);
      composer.setSize(width, height);
      bloom.resolution.set(Math.round(width / 2), Math.round(height / 2));

      const bw = Math.round(width * dpr);
      const bh = Math.round(height * dpr);
      (u.uRes!.value as THREE.Vector2).set(bw, bh);
      u.uScale!.value = Math.min((bw * FIT_W) / SDF.box.w, (bh * FIT_H) / SDF.box.h);
    },

    render({ t, pointer, settled }) {
      u.uTime!.value = t;
      u.uSettle!.value = settled ? 1 : Math.min(1, t / 2.4);
      (u.uPointer!.value as THREE.Vector2).set(pointer[0], pointer[1]);
      finish.uniforms.uTime!.value = t;
      composer.render();
    },

    dispose() {
      quad.geometry.dispose();
      material.dispose();
      sdfTex.dispose();
      composer.dispose();
      renderer.dispose();
    },
  };
}
